import {
  FREDRICK_DATA_PROTOCOL_VERSION,
  FREDRICK_OWL_PAGE_CANDIDATE_TYPE,
  createEvidenceImageId,
  createSourceImageId,
  transcriptionIsPartial,
  type FredrickOwlPageCandidateV1,
  type FredrickPrivateEvidenceDescriptorV1,
  type FredrickSourceImageV1,
} from 'fredrick-data-contract';
import { parseFredrickOwlPageCandidateV1 } from 'fredrick-data-contract/parse';
import {
  OWL_WINDOW_HEIGHT,
  OWL_WINDOW_WIDTH,
  transcribeOwlScreenshot,
} from 'maplestory-owl-transcribe';
import { sha256Digest } from './hash';
import {
  canonicalRgbImage,
  type CanonicalRgbImage,
  cropOwlWindowImage,
  encodeOwlWindowPng,
  fileToPixelImage,
} from './image';

export const FREDRICK_TRANSCRIBER_IMPLEMENTATION = 'maplestory-owl-transcribe' as const;
export const FREDRICK_TRANSCRIBER_IMPLEMENTATION_VERSION = '1.0.0' as const;

export interface PreparedSourceImage {
  readonly image: CanonicalRgbImage;
  readonly sourceImage: FredrickSourceImageV1;
}

export interface PreparedOwlImport {
  readonly sourceImage: FredrickSourceImageV1;
  readonly evidence: FredrickPrivateEvidenceDescriptorV1;
  readonly evidenceBlob: Blob;
  readonly candidate: FredrickOwlPageCandidateV1;
}

export interface CompleteOwlImportOptions {
  readonly observedAt: number;
  readonly importedAt: number;
  readonly revisionId?: string;
  readonly transcribedAt?: number;
}

function timestamp(value: number, field: string): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${field} must be a non-negative safe integer.`);
  }
  return value;
}

export async function prepareSourceImage(file: Blob): Promise<PreparedSourceImage> {
  const decoded = await fileToPixelImage(file);
  const image = canonicalRgbImage(decoded);
  const pixelSha256 = await sha256Digest(image.data);
  const sourceImageId = createSourceImageId({
    width: image.width,
    height: image.height,
    pixelSha256,
  });

  return {
    image,
    sourceImage: {
      sourceImageId,
      pixelSha256,
      width: image.width,
      height: image.height,
    },
  };
}

export async function completeOwlImport(
  prepared: PreparedSourceImage,
  options: CompleteOwlImportOptions,
): Promise<PreparedOwlImport> {
  const observedAt = timestamp(options.observedAt, 'observedAt');
  const importedAt = timestamp(options.importedAt, 'importedAt');
  const transcribedAt = timestamp(options.transcribedAt ?? Date.now(), 'transcribedAt');
  const revisionId = options.revisionId ?? crypto.randomUUID();
  if (!revisionId) throw new TypeError('revisionId must be non-empty.');

  const result = transcribeOwlScreenshot(prepared.image);
  if (!result.ok) throw new Error(result.error.message);

  const owlWindowImage = cropOwlWindowImage(prepared.image, result.owlWindow);
  const evidencePixelSha256 = await sha256Digest(Uint8Array.from(owlWindowImage.data));
  const evidence: FredrickPrivateEvidenceDescriptorV1 = {
    evidenceImageId: createEvidenceImageId(evidencePixelSha256),
    pixelSha256: evidencePixelSha256,
    width: OWL_WINDOW_WIDTH,
    height: OWL_WINDOW_HEIGHT,
  };

  const candidate: FredrickOwlPageCandidateV1 = {
    protocolVersion: FREDRICK_DATA_PROTOCOL_VERSION,
    recordType: FREDRICK_OWL_PAGE_CANDIDATE_TYPE,
    revisionId,
    capture: {
      protocolVersion: FREDRICK_DATA_PROTOCOL_VERSION,
      sourceImage: prepared.sourceImage,
      observedAt,
      importedAt,
    },
    owlWindow: result.owlWindow,
    evidence,
    transcriber: {
      owlContractVersion: 1,
      implementation: FREDRICK_TRANSCRIBER_IMPLEMENTATION,
      implementationVersion: FREDRICK_TRANSCRIBER_IMPLEMENTATION_VERSION,
    },
    transcribedAt,
    transcription: result.transcription,
    partial: transcriptionIsPartial(result.transcription),
  };

  return {
    sourceImage: prepared.sourceImage,
    evidence,
    evidenceBlob: encodeOwlWindowPng(owlWindowImage),
    candidate: parseFredrickOwlPageCandidateV1(candidate),
  };
}
